import { useCartContext } from "app/providers/CartProvider";
import { useMutation } from "@tanstack/react-query";
import { api } from "shared/api";
import { useSearchParams } from "react-router-dom";
import { useOrderContext } from "app/providers/OrderProvider";
import { toast } from "react-toastify";
import { ProductContext } from "shared/utils/productContext";

const apiValidateVoucher = async (voucher: string, model: string) => {
  const res = await api().gersValidateDiscountCode(voucher, model);
  return res.data;
};

const apiSetOrderVoucher = async (orderId: string, voucher?: string) => {
  const res = await api().gersSetOrderVoucher(orderId, voucher);
  return res.data;
};

export const useVoucher = () => {
  const { mutateAsync, isLoading: isLoadingVoucher } =
    useMutation(apiSetOrderVoucher);
  const { setVoucher } = useCartContext();
  const { refetchOrder } = useOrderContext();
  const [searchParams] = useSearchParams();

  const fetchVoucherData = async (voucher: string) => {
    if (voucher === "") throw new Error("Vul een voucher in");

    try {
      const voucherData = await mutateAsync(voucher);
      return voucherData;
    } catch (error) {
      throw new Error("Geen geldige voucher");
    }
  };

  const addVoucher = async (voucher: string, productContext?: string) => {
    const orderId = searchParams.get("orderId");
    if (orderId) {
      await addVoucherToOrder(orderId, voucher);
    } else {
      await addVoucherToCart(voucher, productContext ?? "");
    }
  };

  const handleVoucherTypeOnAddition = (
    voucherData: any,
    productContext: string,
    discountCodeInitialised: boolean,
  ) => {
    if (
      // It's not empty
      (voucherData !== "" &&
        // It's either a door voucher and the cart has doors
        voucherData.applyToOrder &&
        productContext === ProductContext.DoorContext) ||
      // It's either a std product voucher and the cart has std products
      (voucherData.applyToStandardOrder &&
        productContext === ProductContext.StandardProductContext)
    ) {
      // Set the correct voucher
      setVoucher(voucherData, discountCodeInitialised);

      // Show success message
      if (!discountCodeInitialised) {
        toast.success("Voucher is toegevoegd!");
      }
    } else {
      // Show error message
      toast.error("Voucher is ongeldig!");
    }
  };

  const addVoucherToCart = async (
    voucher: string,
    productContext: string,
    discountCodeInitialised = false,
  ) => {
    const voucherData = await fetchVoucherData(voucher);

    handleVoucherTypeOnAddition(
      voucherData,
      productContext,
      discountCodeInitialised,
    );
  };

  const addVoucherToOrder = async (orderId: string, voucher: string) => {
    if (!voucher) {
      await apiSetOrderVoucher(orderId);
    } else {
      await apiSetOrderVoucher(orderId, voucher);
    }
    refetchOrder();
  };

  return { addVoucher, addVoucherToOrder, addVoucherToCart, isLoadingVoucher };
};
